import { Matrix3, Matrix4, Vector3 } from 'three';
import { proximate as m_proximate } from './maths';
import * as m3 from './matrix3';

/** return true if two vectors are proximate (close to each other)
 *
 * - `a` — first vector
 * - `b` — second vector
 * - `e` — acceptable error
 */
export function proximate(a: Vector3, b: Vector3, e: number) {
    return (
        m_proximate(a.x, b.x, e)
        && m_proximate(a.y, b.y, e)
        && m_proximate(a.z, b.z, e)
    );
}

/** returns whether two vectors are exactly equal */
export function eq(a: Vector3, b: Vector3) {
    return (a.x === b.x && a.y === b.y && a.z === b.z);
}

/** return the cross product of two vectors */
export function cross(a: Vector3, b: Vector3): Vector3 {
    return new Vector3().crossVectors(a, b);
}

/** return the dot product of two vectors */
export function dot(a: Vector3, b: Vector3): number {
    return a.dot(b);
}

/** return the length of a vector */
export function len(a: Vector3): number {
    return a.length();
}

/** creates a copy of a vector */
export function copy(x: Vector3): Vector3 {
    return new Vector3().copy(x);
}

/** normalise a vector, returning the normalised vector */
export function normalise(x: Vector3): Vector3 {
    return copy(x).normalize();
}

/** return the angle between two vectors */
export function angle(a: Vector3, b: Vector3) {

    const an = normalise(a);
    const bn = normalise(b);
    const d = Math.min(dot(an, bn), 1.0);

    const theta = Math.abs(Math.acos(d));
    if (theta <= 0.3) {
        return Math.asin(len(cross(an, bn)));
    } else if (theta < 2.7) {
        return theta;
    } else {
        return Math.PI - Math.asin(len(cross(an, bn)));
    }
}


/** return the counter-clockwise angle between two vectors on a plane
 *
 * - `a` — first vector
 * - `b` — second vector
 * - `n` — a normal on their plane, determining the upwards direction
 */
export function cc_angle(a: Vector3, b: Vector3, n: Vector3): number {
    /** acceptable error */
    const e = 1e-8;

    if (
        m_proximate(len(a), 0.0, e)
        || m_proximate(len(b), 0.0, e)
        || m_proximate(len(n), 0.0, e)
    )
        return 0;

    const an = normalise(a);
    const bn = normalise(b);
    const nn = normalise(n);

    if (approximately_coaxial(an, nn, e) || approximately_coaxial(bn, nn, e))
        return 0;

    const phi = angle(an, bn);
    if (m_proximate(phi, 0.0, e))
        return 0;
    else if (m_proximate(phi, Math.PI, e))
        return Math.PI;
    else if (dot(n, cross(an, bn)) >= 0.0)
        return phi;
    else
        return 2.0 * Math.PI - phi;
}

/** true when two vectors are coaxial or almost coaxial
 * (parallel or antiparallel)
 *
 * - `a` — first vector
 * - `b` — second vector
 * - `e` — acceptable error
 */
export function approximately_coaxial(a: Vector3, b: Vector3, e: number): boolean {
    const t = angle(a, b);
    return m_proximate(t, 0.0, e)
        || m_proximate(t, Math.PI, e);
}

/** create vector from lattitude (θ) and longitude (φ) */
export function fromAngles(theta: number, phi: number): Vector3 {
    return new Vector3(
        Math.sin(theta) * Math.cos(phi),
        Math.sin(theta) * Math.sin(phi),
        Math.cos(theta),
    );
}

/** vectors representing the primary 3d axes */
export const Axes = {
    X: new Vector3(1, 0, 0),
    Y: new Vector3(0, 1, 0),
    Z: new Vector3(0, 0, 1),
} as const;

/** return lattitude and longitude of a vector */
export function toAngles(d: Vector3): [number, number] {
    if (d.x === 0 && d.y === 0)
        return [0, 0];

    const theta = angle(d, Axes.Z);
    const phi = m_proximate(theta, 0.0, 1e-8) || m_proximate(theta, Math.PI, 1e-8)
        ? 0
        : cc_angle(
            Axes.X,
            new Vector3(d.x, d.y, 0),
            Axes.Z
        );
    return [theta, phi];
}

/** return a vector multiplied by a matrix */
export function multMatrix(v: Vector3, m: Matrix3): Vector3 {
    return copy(v).applyMatrix3(m);
}

/** subtract two vectors, returning a new vector */
export const sub = (a: Vector3, b: Vector3) =>
    new Vector3().subVectors(a, b);

/** add two vectors, returning a new vector */
export const add = (a: Vector3, b: Vector3) =>
    copy(a).add(b);

/** generates a completely random vector */
export const random = () => new Vector3(Math.random(), Math.random(), Math.random());

/** convert a vector to an array */
export const toArray = (x: Vector3): [number, number, number] => [x.x, x.y, x.z];

/** calculate the distance between two points in 3d space */
export const point_point_distance = (a: Vector3, b: Vector3) =>
    Math.sqrt(
        (a.x - b.x)**2 + (a.y - b.y)**2 + (a.z - b.z)**2
    );

/** Projects point p on line with base b and direction d.
 *
 * d is assumed to be unit.
 */
export function line_point_distance(b: Vector3, d: Vector3, p: Vector3): number {
    const x = add(b, d);
    const v1 = sub(p, b);
    const v2 = sub(p, x);
    return len(cross(v1, v2)) / len(sub(x, b));
}

/** Projects point p on line with base b and direction d.
 *
 * d is assumed to be unit.
 */
export const project_point_on_line = (p: Vector3, b: Vector3, d: Vector3): Vector3 =>
    copy(b).addScaledVector(d, dot(sub(p, b), d));

/** Projects point p on plane with base b and normal n.
 *
 * n is assumed to be unit.
 */
export const project_point_on_plane = (p: Vector3, b: Vector3, n: Vector3): Vector3 =>
    copy(p).addScaledVector(n, -1 * dot(sub(p, b), n));


/** Rotates vector v by applying transformation matrix R to it.
 *
 * R is assumed to be a valid rotation matrix.
 */
export function rotate_vector_by_rotation_matrix(v: Vector3, R: Matrix3) {
    const m4 = new Matrix4();
    m4.set(
        R.elements[0]!, R.elements[3]!, R.elements[6]!, 0,
        R.elements[1]!, R.elements[4]!, R.elements[7]!, 0,
        R.elements[2]!, R.elements[5]!, R.elements[8]!, 0,
        0, 0, 0, 1,
    );
    const new_v = new Vector3().copy(v);
    new_v.applyMatrix4(m4);
    return new_v;
}

/** Rotates vector v around axis by angle.
 *
 * axis is assumed to be a non-0 vector. angle is given in radians
 */
export function rotate_vector_around_axis(v: Vector3, axis: Vector3, angle: number) {
    const R = m3.vectorRotation(axis, angle);
    const new_v = rotate_vector_by_rotation_matrix(v, R);
    return new_v;
}

/** Returns the "best" (most sensible) orthogonal vector direction for a vector v
 *
 * v is assumed to be a non-0 vector.
 */
export function find_best_orthogonal_direction_for_vector(v: Vector3) {
    const vd = new Vector3().copy(v).normalize();
    const vx = new Vector3(1, 0, 0);
    const vy = new Vector3(0, 1, 0);
    const vz = new Vector3(0, 0, 1);
    const dx = Math.abs(vx.dot(vd));
    const dy = Math.abs(vy.dot(vd));
    const dz = Math.abs(vz.dot(vd));
    const vorth = new Vector3();
    const vtmp = new Vector3();
    if (dx >= dy && dx >= dz) {
        vtmp.crossVectors(vd, vz).normalize();
        vorth.crossVectors(vd, vtmp);
    } else if (dy >= dx && dy >= dz) {
        vtmp.crossVectors(vd, vx).normalize();
        vorth.crossVectors(vd, vtmp);
    } else {
        vtmp.crossVectors(vd, vy).normalize();
        vorth.crossVectors(vd, vtmp);
    }
    return vorth;
}
